import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../shared/components/modals/ConfirmationModal';
import ActionMenu from '../../shared/components/common/ActionMenu';
import StatsCard from '../../shared/components/common/StatsCard';
import { StatusBadge } from '../../shared/components/common/Badges';
import { formatRoleName } from '../../utils/roleFormatter';
import { toBoolean } from '../../utils/booleanUtils';
import { formatCurrencyDynamic } from '../../utils/numberUtils';
import { API_ENDPOINTS } from '../../config/apiConfig';
import LocationEditModal from './LocationEditModal';
import LocationViewModal from './LocationViewModal';
import LocationsCreateWizard from './LocationsCreateWizard';
import LocationDeleteBlockedModal from './LocationDeleteBlockedModal';
import './LocationsPage.css';
import '../../shared/components/common/ActorDisplay.css';
import '../../shared/components/common/StatsCard.css';
import '../../styles/filters-ui.css';

const FILTER_ALL = 'all';
const FILTER_ACTIVE = 'active';
const FILTER_INACTIVE = 'inactive';
const FILTER_NEEDS_SETUP = 'needs_setup';
const FILTER_BUDGET_MISSING = 'budget_missing';
const FILTER_TAT_MISSING = 'tat_missing';
const FILTER_THRESHOLD_MISSING = 'threshold_missing';
const FILTER_HAS_ISSUES = 'has_issues';

const SORT_LOCATION = 'location';
const SORT_STATUS = 'status';
const SORT_USERS = 'users';
const SORT_DEVICES = 'devices';
const SORT_ISSUES = 'issues';
const SORT_BUDGET = 'budget';
const SORT_TAT = 'tat';
const SORT_THRESHOLD = 'threshold';

const EMPTY_SUMMARY = {
  total: 0,
  active: 0,
  inactive: 0,
  needs_setup: 0,
  active_complaints: 0,
};

function ConfigBadge({ configured, label = 'Configured' }) {
  if (configured) {
    return (
      <span
        className="loc-config-ok"
        title={label}
        aria-label={label}
      >
        ✓
      </span>
    );
  }
  return (
    <span className="loc-config-badge loc-config-badge--warn">Missing</span>
  );
}

function LocationsManagement({
  token,
  user,
  onViewAllDevicesAtLocation,
  onViewAllUsersAtLocation,
}) {
  const [locations, setLocations] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingLocationId, setViewingLocationId] = useState(null);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, location: null });
  const [isDeletingLocation, setIsDeletingLocation] = useState(false);
  const [deleteBlockedInfo, setDeleteBlockedInfo] = useState(null);
  const [toggleConfirmation, setToggleConfirmation] = useState({ isOpen: false, location: null, action: '' });
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const [sortBy, setSortBy] = useState(SORT_LOCATION);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.LOCATIONS.OVERVIEW, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(response.data?.summary || EMPTY_SUMMARY);
      setLocations(response.data?.locations || []);
    } catch (error) {
      console.error('Error fetching locations overview:', error);
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const filteredLocations = useMemo(() => {
    let result = [...locations];
    const query = searchQuery.trim().toLowerCase();

    if (query) {
      result = result.filter((loc) => {
        const haystack = [
          loc.name,
          loc.location_code,
          loc.city,
          loc.contact_email,
          loc.primary_admin_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
    }

    switch (statusFilter) {
      case FILTER_ACTIVE:
        result = result.filter((loc) => toBoolean(loc.is_active));
        break;
      case FILTER_INACTIVE:
        result = result.filter((loc) => !toBoolean(loc.is_active));
        break;
      case FILTER_NEEDS_SETUP:
        result = result.filter((loc) => loc.needs_setup);
        break;
      case FILTER_BUDGET_MISSING:
        result = result.filter((loc) => !loc.budget_configured);
        break;
      case FILTER_TAT_MISSING:
        result = result.filter((loc) => !loc.tat_configured);
        break;
      case FILTER_THRESHOLD_MISSING:
        result = result.filter((loc) => !loc.high_cost_threshold_configured);
        break;
      case FILTER_HAS_ISSUES:
        result = result.filter((loc) => (loc.active_complaints || 0) > 0);
        break;
      default:
        break;
    }

    result.sort((a, b) => {
      let cmp = 0;

      switch (sortBy) {
        case SORT_STATUS:
          cmp = Number(toBoolean(a.is_active)) - Number(toBoolean(b.is_active));
          break;
        case SORT_USERS:
          cmp = (a.user_count || 0) - (b.user_count || 0);
          break;
        case SORT_DEVICES:
          cmp = (a.device_count || 0) - (b.device_count || 0);
          break;
        case SORT_ISSUES:
          cmp = (a.active_complaints || 0) - (b.active_complaints || 0);
          break;
        case SORT_BUDGET: {
          const aBudget = a.budget_configured ? Number(a.annual_budget) || 0 : -1;
          const bBudget = b.budget_configured ? Number(b.annual_budget) || 0 : -1;
          cmp = aBudget - bBudget;
          break;
        }
        case SORT_TAT:
          cmp = Number(Boolean(a.tat_configured)) - Number(Boolean(b.tat_configured));
          break;
        case SORT_THRESHOLD:
          cmp = Number(Boolean(a.high_cost_threshold_configured)) - Number(Boolean(b.high_cost_threshold_configured));
          break;
        case SORT_LOCATION:
        default:
          cmp = String(a.name).localeCompare(String(b.name));
          break;
      }

      if (cmp === 0 && sortBy !== SORT_LOCATION) {
        cmp = String(a.name).localeCompare(String(b.name));
      }

      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [locations, searchQuery, statusFilter, sortBy, sortDirection]);

  const handleStatsCardClick = (filter) => {
    setStatusFilter(filter);
  };

  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDirection('asc');
  };

  const getSortIndicator = (column) => {
    if (sortBy !== column) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const handleToggleStatusClick = (location) => {
    const isCurrentlyActive = toBoolean(location.is_active);
    const newIsActive = isCurrentlyActive ? 0 : 1;
    const action = newIsActive === 1 ? 'enable' : 'disable';
    setToggleConfirmation({
      isOpen: true,
      location,
      action,
      newIsActive,
    });
  };

  const handleToggleStatusConfirm = async () => {
    const { location, newIsActive, action } = toggleConfirmation;
    setIsTogglingStatus(true);
    try {
      const endpoint = newIsActive === 1
        ? `${API_ENDPOINTS.LOCATIONS.GET(location.id).replace(/\/[^/]*$/, '')}/${location.id}/enable`
        : `${API_ENDPOINTS.LOCATIONS.GET(location.id).replace(/\/[^/]*$/, '')}/${location.id}/disable`;

      await axios.post(endpoint, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      toast.success(`Location ${action === 'enable' ? 'enabled' : 'disabled'} successfully!`);
      await fetchOverview();
      setToggleConfirmation({ isOpen: false, location: null, action: '' });
    } catch (error) {
      console.error('Error toggling location status:', error);
      toast.error(error.response?.data?.error || 'Failed to update location status');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleDeleteLocationConfirm = async () => {
    const location = deleteConfirmation.location;
    if (!location) return;
    setIsDeletingLocation(true);
    try {
      await axios.delete(API_ENDPOINTS.LOCATIONS.DELETE(location.id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Location deleted successfully!');
      setDeleteConfirmation({ isOpen: false, location: null });
      await fetchOverview();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (status === 400 && (data?.device_count != null || data?.user_count != null)) {
        setDeleteConfirmation({ isOpen: false, location: null });
        setDeleteBlockedInfo({
          ...data,
          is_active: data.is_active ?? location?.is_active,
        });
      } else {
        toast.error(data?.error || 'Failed to delete location');
      }
    } finally {
      setIsDeletingLocation(false);
    }
  };

  const filterChips = [
    { id: FILTER_ALL, label: 'All' },
    { id: FILTER_ACTIVE, label: 'Active' },
    { id: FILTER_INACTIVE, label: 'Inactive' },
    { id: FILTER_NEEDS_SETUP, label: 'Needs setup' },
    { id: FILTER_BUDGET_MISSING, label: 'Budget missing' },
    { id: FILTER_TAT_MISSING, label: 'TAT missing' },
    { id: FILTER_THRESHOLD_MISSING, label: 'Threshold missing' },
    { id: FILTER_HAS_ISSUES, label: 'Has active issues' },
  ];

  return (
    <div className="locations-management">
      <div className="locations-description-section">
        <h1>🏥 Location Management</h1>
        <p>Manage all locations, monitor readiness, and fix setup gaps quickly.</p>
      </div>

      <div className="locations-header">
        <div className="header-left">
          <div className="stats-summary locations-stats-summary">
            <div
              className={`stats-card-wrapper ${statusFilter === FILTER_ALL ? 'active' : ''}`.trim()}
              onClick={() => handleStatsCardClick(FILTER_ALL)}
            >
              <StatsCard icon="🏥" number={summary.total} label="Total Locations" className="total" />
            </div>
            <div
              className={`stats-card-wrapper ${statusFilter === FILTER_ACTIVE ? 'active' : ''}`.trim()}
              onClick={() => handleStatsCardClick(FILTER_ACTIVE)}
            >
              <StatsCard icon="✅" number={summary.active} label="Active" className="active" />
            </div>
            <div
              className={`stats-card-wrapper ${statusFilter === FILTER_INACTIVE ? 'active' : ''}`.trim()}
              onClick={() => handleStatsCardClick(FILTER_INACTIVE)}
            >
              <StatsCard icon="⏸️" number={summary.inactive} label="Inactive" className="inactive" />
            </div>
            <div
              className={`stats-card-wrapper ${statusFilter === FILTER_NEEDS_SETUP ? 'active' : ''}`.trim()}
              onClick={() => handleStatsCardClick(FILTER_NEEDS_SETUP)}
            >
              <StatsCard icon="⚠️" number={summary.needs_setup} label="Needs Setup" className="password-setup" />
            </div>
            <div
              className={`stats-card-wrapper ${statusFilter === FILTER_HAS_ISSUES ? 'active' : ''}`.trim()}
              onClick={() => handleStatsCardClick(FILTER_HAS_ISSUES)}
            >
              <StatsCard icon="🛠️" number={summary.active_complaints} label="Active Issues" className="open" />
            </div>
          </div>
        </div>
        <div className="header-right">
          <div className="action-palette">
            <button type="button" className="action-btn action-primary" onClick={() => setShowCreateModal(true)}>
              <span className="action-icon">➕</span>
              <span className="action-text">Add New Location</span>
            </button>
          </div>
        </div>
      </div>

      <div className="search-section">
        <div className="search-box-wrapper">
          <span className="search-icon-lens" aria-hidden="true">🔍</span>
          <input
            type="text"
            placeholder="Search by name, code, city, email, or admin..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-clean"
          />
          {searchQuery && (
            <button
              type="button"
              className="clear-search-btn-inline"
              onClick={() => setSearchQuery('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="quick-filters">
        <div className="quick-filters-title">
          <svg className="filters-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Quick Filters
        </div>
        <div className="preset-buttons">
          {filterChips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              className={`preset-btn ${statusFilter === chip.id ? 'active' : ''}`}
              onClick={() => setStatusFilter(chip.id)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="loading-container">
          <p>Loading locations...</p>
        </div>
      )}

      {!loading && (
        <>
          <div className="locations-results-info">
            Showing {filteredLocations.length} of {locations.length} location{locations.length === 1 ? '' : 's'}
          </div>

          <div className="locations-table-container">
            {filteredLocations.length > 0 ? (
              <table className="locations-table">
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSortChange(SORT_LOCATION)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by location name"
                    >
                      Location{getSortIndicator(SORT_LOCATION)}
                    </th>
                    <th
                      className="center"
                      onClick={() => handleSortChange(SORT_STATUS)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by status"
                    >
                      Status{getSortIndicator(SORT_STATUS)}
                    </th>
                    <th
                      className="center locations-table-num"
                      onClick={() => handleSortChange(SORT_USERS)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by user count"
                    >
                      Users{getSortIndicator(SORT_USERS)}
                    </th>
                    <th
                      className="center locations-table-num"
                      onClick={() => handleSortChange(SORT_DEVICES)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by device count"
                    >
                      Devices{getSortIndicator(SORT_DEVICES)}
                    </th>
                    <th
                      className="center locations-table-num"
                      onClick={() => handleSortChange(SORT_ISSUES)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by open issues"
                    >
                      Open issues{getSortIndicator(SORT_ISSUES)}
                    </th>
                    <th
                      className="center"
                      onClick={() => handleSortChange(SORT_BUDGET)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by budget amount (missing budgets sort first ascending)"
                    >
                      Budget{getSortIndicator(SORT_BUDGET)}
                    </th>
                    <th
                      className="center"
                      onClick={() => handleSortChange(SORT_TAT)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Complaint TAT configuration"
                    >
                      Complaint TAT{getSortIndicator(SORT_TAT)}
                    </th>
                    <th
                      className="center"
                      onClick={() => handleSortChange(SORT_THRESHOLD)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by approval threshold configuration"
                    >
                      Approval threshold{getSortIndicator(SORT_THRESHOLD)}
                    </th>
                    <th className="center col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.map((location) => {
                    const isActive = toBoolean(location.is_active);
                    const handleViewLocation = () => setViewingLocationId(location.id);
                    return (
                      <tr
                        key={location.id}
                        className={`location-row-clickable${!isActive ? ' inactive-location' : ''}`}
                        onClick={handleViewLocation}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewLocation();
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        title={`View ${location.name}`}
                        aria-label={`View details for ${location.name}`}
                      >
                        <td>
                          <div className="locations-table-location-cell">
                            <strong>{location.name}</strong>
                            <span className="locations-table-subtext">
                              {location.location_code}
                              {location.city ? ` · ${location.city}` : ''}
                            </span>
                            {location.primary_admin_name && (
                              <span className="actor-display-inline actor-display-inline--compact locations-table-admin-actor">
                                <span className="actor-display-name">{location.primary_admin_name}</span>
                                <span className="actor-role-badge">{formatRoleName('admin')}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="center">
                          <StatusBadge status={isActive ? 'active' : 'inactive'} size="small" />
                        </td>
                        <td className="center locations-table-num">{location.user_count ?? 0}</td>
                        <td className="center locations-table-num">{location.device_count ?? 0}</td>
                        <td className="center locations-table-num">{location.active_complaints ?? 0}</td>
                        <td className="center">
                          {location.budget_configured ? (
                            <span className="locations-table-budget">
                              {formatCurrencyDynamic(location.annual_budget)}
                            </span>
                          ) : (
                            <ConfigBadge configured={false} />
                          )}
                        </td>
                        <td className="center"><ConfigBadge configured={location.tat_configured} label="Complaint TAT configured" /></td>
                        <td className="center"><ConfigBadge configured={location.high_cost_threshold_configured} label="Approval threshold configured" /></td>
                        <td className="actions center" onClick={(e) => e.stopPropagation()}>
                          <ActionMenu
                            ariaLabel={`Actions for ${location.name}`}
                            actions={[
                              {
                                id: 'view',
                                icon: '👁',
                                label: 'View',
                                onClick: () => setViewingLocationId(location.id),
                              },
                              {
                                id: 'edit',
                                icon: '✏️',
                                label: 'Edit',
                                onClick: () => setEditingLocationId(location.id),
                              },
                              {
                                id: 'toggle',
                                icon: isActive ? '🚫' : '✓',
                                label: isActive ? 'Disable' : 'Enable',
                                danger: isActive,
                                onClick: () => handleToggleStatusClick(location),
                              },
                              {
                                id: 'delete',
                                icon: '🗑️',
                                label: 'Delete',
                                danger: true,
                                onClick: () => setDeleteConfirmation({ isOpen: true, location }),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <p>
                  {locations.length === 0
                    ? 'No locations found. Create your first location to get started!'
                    : 'No locations match your search or filters.'}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      <LocationsCreateWizard
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchOverview}
        token={token}
      />

      <LocationViewModal
        isOpen={viewingLocationId !== null}
        locationId={viewingLocationId}
        token={token}
        onClose={() => setViewingLocationId(null)}
        currentUser={user}
      />

      <LocationEditModal
        isOpen={editingLocationId !== null}
        locationId={editingLocationId}
        token={token}
        onClose={async () => {
          setEditingLocationId(null);
          await fetchOverview();
        }}
        currentUser={user}
      />

      <ConfirmationModal
        isOpen={toggleConfirmation.isOpen}
        entityName={toggleConfirmation.location?.name}
        entityType="location"
        action={toggleConfirmation.action}
        onConfirm={handleToggleStatusConfirm}
        onCancel={() => setToggleConfirmation({ isOpen: false, location: null, action: '' })}
        isLoading={isTogglingStatus}
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        entityName={deleteConfirmation.location?.name}
        entityType="location"
        action="delete"
        onConfirm={handleDeleteLocationConfirm}
        onCancel={() => {
          if (!isDeletingLocation) setDeleteConfirmation({ isOpen: false, location: null });
        }}
        isLoading={isDeletingLocation}
        customMessage="You can delete this location only if it has no users and no devices."
      />

      <LocationDeleteBlockedModal
        blockedInfo={deleteBlockedInfo}
        onClose={() => setDeleteBlockedInfo(null)}
        onViewAllDevices={onViewAllDevicesAtLocation}
        onViewAllUsers={onViewAllUsersAtLocation}
      />
    </div>
  );
}

export default LocationsManagement;
